import { useEffect, useRef, useState, useCallback } from "react";
import {
  BoxRect,
  DRAG_THRESHOLD_PX,
  TalhaoBounds,
  clientRectToContainerRect,
  clientRectToMapDivRect,
  getContainedTalhaoIds,
  getDistance,
  getFeaturesContainedInBounds,
  pixelBoundsToLatLngBounds,
} from "./boxSelectUtils";

type UseTalhaoBoxSelectParams = {
  mapRef: React.MutableRefObject<google.maps.Map | null>;
  boundsByIdRef?: React.MutableRefObject<Map<string, TalhaoBounds>>;
  enabled?: boolean;
  containerId?: string;
  onSelect: (talhaoIds: string[]) => void;
};

const SKIP_CLICK_MS = 400;

export function useTalhaoBoxSelect({
  mapRef,
  boundsByIdRef,
  enabled = true,
  containerId = "map-print-area",
  onSelect,
}: UseTalhaoBoxSelectParams) {
  const [boxRect, setBoxRect] = useState<BoxRect | null>(null);
  const dragState = useRef<{
    active: boolean;
    startX: number;
    startY: number;
    isDragging: boolean;
  } | null>(null);

  const projectionRef = useRef<google.maps.MapCanvasProjection | null>(null);
  const skipNextClickRef = useRef(false);
  const skipTimeoutRef = useRef<number | null>(null);
  const ctrlHeldRef = useRef(false);

  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const applyMapInteractionState = useCallback((map: google.maps.Map) => {
    const selectingBox = dragState.current?.isDragging ?? false;
    const shouldBlock = ctrlHeldRef.current || selectingBox;

    if (shouldBlock) {
      const cursor =
        selectingBox || ctrlHeldRef.current ? "crosshair" : "default";
      map.setOptions({
        draggable: false,
        gestureHandling: "none",
        draggableCursor: cursor,
        draggingCursor: cursor,
      });
      return;
    }

    map.setOptions({
      draggable: true,
      gestureHandling: "greedy",
      draggableCursor: null,
      draggingCursor: null,
    });

    const mapDiv = map.getDiv() as HTMLElement | null;
    if (mapDiv) {
      mapDiv.style.removeProperty("cursor");
    }
  }, []);

  const armSkipNextClick = useCallback(() => {
    skipNextClickRef.current = true;
    if (skipTimeoutRef.current != null) {
      window.clearTimeout(skipTimeoutRef.current);
    }
    skipTimeoutRef.current = window.setTimeout(() => {
      skipNextClickRef.current = false;
      skipTimeoutRef.current = null;
    }, SKIP_CLICK_MS);
  }, []);

  const clearPointerGesture = useCallback(() => {
    dragState.current = null;
    setBoxRect(null);
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !enabled) return;

    const overlay = new google.maps.OverlayView();
    overlay.onAdd = () => {
      projectionRef.current = overlay.getProjection() || null;
    };
    overlay.draw = () => {
      const projection = overlay.getProjection();
      if (projection) {
        projectionRef.current = projection;
      }
    };
    overlay.setMap(map);

    return () => {
      overlay.setMap(null);
      projectionRef.current = null;
    };
  }, [mapRef, enabled]);

  useEffect(() => {
    if (!enabled) return;

    const container = document.getElementById(containerId);
    if (!container) return;

    function handleMouseDown(e: MouseEvent) {
      if (e.button !== 0) return;

      if (!e.ctrlKey) {
        // Panning the map must clear any stale synthetic-click guard from a
        // previous box selection.
        skipNextClickRef.current = false;
        if (skipTimeoutRef.current != null) {
          window.clearTimeout(skipTimeoutRef.current);
          skipTimeoutRef.current = null;
        }
        return;
      }

      const map = mapRef.current;
      if (!map) return;

      const target = e.target as Node;
      if (!container!.contains(target)) return;

      // A later Ctrl+click must never inherit the synthetic-click guard
      // from a previous box gesture.
      skipNextClickRef.current = false;
      if (skipTimeoutRef.current != null) {
        window.clearTimeout(skipTimeoutRef.current);
        skipTimeoutRef.current = null;
      }
      ctrlHeldRef.current = true;
      dragState.current = {
        active: true,
        startX: e.clientX,
        startY: e.clientY,
        isDragging: false,
      };
      applyMapInteractionState(map);
    }

    function handleMouseMove(e: MouseEvent) {
      const state = dragState.current;
      if (!state?.active) return;

      const map = mapRef.current;
      if (!map) return;

      const distance = getDistance(state.startX, state.startY, e.clientX, e.clientY);

      if (!state.isDragging && distance >= DRAG_THRESHOLD_PX) {
        state.isDragging = true;
        e.preventDefault();
        e.stopPropagation();
        armSkipNextClick();
        applyMapInteractionState(map);
      }

      if (!state.isDragging) return;

      e.preventDefault();
      e.stopPropagation();
      setBoxRect(
        clientRectToContainerRect(
          container!,
          e.clientX,
          e.clientY,
          state.startX,
          state.startY
        )
      );
    }

    function handleMouseUp(e: MouseEvent) {
      const state = dragState.current;
      if (!state?.active) return;

      const map = mapRef.current;
      const wasDragging = state.isDragging;

      clearPointerGesture();

      // Release the interaction lock after every mouse gesture. A new
      // Ctrl+click will arm it again on its own mousedown.
      ctrlHeldRef.current = false;

      if (map) {
        applyMapInteractionState(map);
      }

      if (!wasDragging) {
        return;
      }
      if (!map) {
        return;
      }

      const rect = clientRectToContainerRect(
        container!,
        e.clientX,
        e.clientY,
        state.startX,
        state.startY
      );

      if (rect.width < DRAG_THRESHOLD_PX && rect.height < DRAG_THRESHOLD_PX) {
        return;
      }

      const projection = projectionRef.current;
      if (!projection) {
        return;
      }

      const mapDivRect = clientRectToMapDivRect(
        map.getDiv() as HTMLElement,
        e.clientX,
        e.clientY,
        state.startX,
        state.startY
      );

      const latLngBounds = pixelBoundsToLatLngBounds(projection, mapDivRect);
      if (!latLngBounds) {
        return;
      }

      const boundsIndex = boundsByIdRef?.current;
      const talhaoIds = boundsIndex
        ? getContainedTalhaoIds(boundsIndex, latLngBounds)
        : getFeaturesContainedInBounds(map, latLngBounds)
            .map((feature) => feature.getProperty("id"))
            .filter((id): id is string | number => id != null && id !== "")
            .map((id) => String(id));

      if (talhaoIds.length === 0) {
        return;
      }

      // Apply the selection before releasing the gesture so the plots are
      // highlighted in the same interaction that closes the box.
      onSelectRef.current(talhaoIds);
    }

    function handleClick(e: MouseEvent) {
      if (!skipNextClickRef.current) return;
      skipNextClickRef.current = false;
      if (skipTimeoutRef.current != null) {
        window.clearTimeout(skipTimeoutRef.current);
        skipTimeoutRef.current = null;
      }
      e.preventDefault();
      e.stopPropagation();
    }

    function handleCancelGesture() {
      ctrlHeldRef.current = false;
      clearPointerGesture();
      const map = mapRef.current;
      if (map) applyMapInteractionState(map);
    }

    function handleKeyUp(e: KeyboardEvent) {
      if (e.key !== "Control") return;
      ctrlHeldRef.current = false;
      const map = mapRef.current;
      if (map) applyMapInteractionState(map);
    }

    container.addEventListener("mousedown", handleMouseDown, true);
    window.addEventListener("mousemove", handleMouseMove, true);
    window.addEventListener("mouseup", handleMouseUp, true);
    window.addEventListener("keyup", handleKeyUp, true);
    window.addEventListener("blur", handleCancelGesture);
    document.addEventListener("visibilitychange", handleCancelGesture);
    window.addEventListener("click", handleClick, true);
    const mapDragEndListener = mapRef.current?.addListener(
      "dragend",
      handleCancelGesture
    );

    return () => {
      container.removeEventListener("mousedown", handleMouseDown, true);
      window.removeEventListener("mousemove", handleMouseMove, true);
      window.removeEventListener("mouseup", handleMouseUp, true);
      window.removeEventListener("keyup", handleKeyUp, true);
      window.removeEventListener("blur", handleCancelGesture);
      document.removeEventListener("visibilitychange", handleCancelGesture);
      window.removeEventListener("click", handleClick, true);
      mapDragEndListener?.remove();

      if (skipTimeoutRef.current != null) {
        window.clearTimeout(skipTimeoutRef.current);
        skipTimeoutRef.current = null;
      }

      ctrlHeldRef.current = false;
      clearPointerGesture();
      const map = mapRef.current;
      if (map) applyMapInteractionState(map);
    };
  }, [
    enabled,
    containerId,
    mapRef,
    boundsByIdRef,
    applyMapInteractionState,
    clearPointerGesture,
    armSkipNextClick,
  ]);

  const getProjection = useCallback(
    () => projectionRef.current,
    []
  );

  return {
    boxRect,
    skipNextClickRef,
    armSkipNextClick,
    getProjection,
  };
}
